import { ClientEvent, RoomEvent } from "matrix-js-sdk";

import { t } from "./i18n";
import { stripReplyFallback } from "./search_text";

export const activeOverlays = new Map(); // group → { close }

// A device whose only pointer is a finger: phones, tablets without a mouse or
// trackpad. Everything that would pull up a software keyboard or has no
// right-click branches on this.
export function isTouchDevice() {
  return window.matchMedia("(pointer: coarse)").matches
      && !window.matchMedia("(pointer: fine)").matches;
}

// A room Rails just created only reaches this client with the next sync, so it
// is not in the store the moment Rails answers. Wait for the SDK to announce it
// rather than polling getRoom() every 500ms; the timeout is a backstop for a
// sync that never delivers (then the caller gets whatever the store holds).
//
// "Announce" means joined, not merely known: Rails provisions with an invite
// and then a join, and the SDK usually learns of the invite a sync before the
// join. A message sent in between goes into a timeline the join then resets,
// and the server's copy of it comes back as a new event next to the local
// echo — the first message of a fresh chat showed twice until a reload.
export function waitForRoom(client, roomId, timeoutMs = 10000) {
  const joined = (room) => room && room.getMyMembership() === "join";

  const existing = client.getRoom(roomId);
  if (joined(existing)) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const cleanup = () => {
      clearTimeout(timer);
      client.removeListener(ClientEvent.Room, onRoom);
      client.removeListener(RoomEvent.MyMembership, onMembership);
    };

    const onRoom = (room) => {
      if (room.roomId !== roomId || !joined(room)) return;
      cleanup();
      resolve(room);
    };

    const onMembership = (room) => onRoom(room);

    const timer = setTimeout(() => {
      cleanup();
      resolve(client.getRoom(roomId));
    }, timeoutMs);

    client.on(ClientEvent.Room, onRoom);
    client.on(RoomEvent.MyMembership, onMembership);
  });
}

// Same event the notification bell's poll emits — the badges listen for it and
// do not care who counted. Both numbers travel together because chat messages
// are ordinary notifications that happen to feed a second badge.
export function broadcastUnreadCounts(count, chatCount) {
  document.dispatchEvent(
    new CustomEvent("notifications:unread-count-updated", {
      detail: { count: Number(count) || 0, chatCount: Number(chatCount) || 0 },
    })
  );
}

export function createInlineOverlay({ anchor, content, group = "default", align = "right", gap = 4, onClose = null }) {
  if (activeOverlays.has(group)) {
    const existing = activeOverlays.get(group);
    const isSameAnchor = (existing.anchor === anchor);
    existing.close();
    if (isSameAnchor) return null;
  }

  const overlay = document.createElement("div");
  overlay.className = "bryght-inline-overlay";
  overlay.appendChild(content);
  document.body.appendChild(overlay);

  const positionOverlay = () => {
    const anchorRect = anchor.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();

    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const openAbove = spaceBelow < overlayRect.height + gap && spaceAbove > spaceBelow;

    if (openAbove) {
      overlay.style.top = `${anchorRect.top + window.scrollY - overlayRect.height - gap}px`;
      overlay.dataset.direction = "above";
    } else {
      overlay.style.top = `${anchorRect.bottom + window.scrollY + gap}px`;
      overlay.dataset.direction = "below";
    }

    if (align === "right") {
      let left = anchorRect.right - overlayRect.width;
      if (left < 8) left = 8;
      overlay.style.left = `${left + window.scrollX}px`;
    } else if (align === "left") {
      let left = anchorRect.left;
      if (left + overlayRect.width > window.innerWidth - 8) {
        left = window.innerWidth - overlayRect.width - 8;
      }
      overlay.style.left = `${left + window.scrollX}px`;
    } else {
      let left = anchorRect.left + (anchorRect.width - overlayRect.width) / 2;
      if (left < 8) left = 8;
      if (left + overlayRect.width > window.innerWidth - 8) {
        left = window.innerWidth - overlayRect.width - 8;
      }
      overlay.style.left = `${left + window.scrollX}px`;
    }
  };

  requestAnimationFrame(() => {
    positionOverlay();
    overlay.classList.add("bryght-inline-overlay--visible");
  });

  let isClosed = false;

  const close = () => {
    if (isClosed) return;
    isClosed = true;
    overlay.remove();
    document.removeEventListener("click", outsideClickHandler);
    window.removeEventListener("resize", positionOverlay);
    if (activeOverlays.get(group)?.element === overlay) {
      activeOverlays.delete(group);
    }
    if (onClose) onClose();
  };

  const outsideClickHandler = (e) => {
    if (!overlay.contains(e.target) && !anchor.contains(e.target)) {
      close();
    }
  };
  
  setTimeout(() => {
    if (!isClosed) {
      document.addEventListener("click", outsideClickHandler);
    }
  }, 0);

  window.addEventListener("resize", positionOverlay);

  const reposition = () => requestAnimationFrame(positionOverlay);

  activeOverlays.set(group, { close, element: overlay, anchor });

  return { close, element: overlay, reposition };
}

export function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export function formatTime(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString("de-DE", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDateDivider(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dStart = startOfDay(date);
  const nowStart = startOfDay(now);
  const diffDays = Math.round((nowStart - dStart) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return t("today", "Heute");
  }
  if (diffDays === 1) {
    return t("yesterday", "Gestern");
  }
  if (diffDays > 1 && diffDays < 7) {
    const weekday = date.toLocaleDateString("de-DE", { weekday: "long" });
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
}

export function getDayKey(timestamp) {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const URL_REGEX = /(?:https?:\/\/|www\.)[^\s]+|\b[a-zA-Z0-9.-]+\.(?:com|de|org|net|io|co|app|me|at|ch|info|biz|eu|tv)\b(?:\/[^\s]*)?/gi;

export function extractFirstUrl(text) {
  if (!text) return null;
  const match = text.match(URL_REGEX);
  if (match) {
    let url = match[0];
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    return url;
  }
  return null;
}

// Every link in a text, found the way linkifyText finds them.
export function extractUrls(text) {
  if (!text) return [];
  return (text.match(URL_REGEX) || []).map((url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`));
}

export function linkifyText(text) {
  if (!text) return document.createTextNode("");
  const fragment = document.createDocumentFragment();
  let lastIndex = 0;
  
  text.replace(URL_REGEX, (url, index) => {
    if (index > lastIndex) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex, index)));
    }
    const a = document.createElement("a");
    let href = url;
    if (!/^https?:\/\//i.test(href)) {
      href = "https://" + href;
    }
    a.href = href;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = url;
    a.className = "bryght-message-link";
    a.addEventListener("pointerdown", (e) => e.stopPropagation());
    fragment.appendChild(a);
    lastIndex = index + url.length;
    return url;
  });
  
  if (lastIndex < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
  return fragment;
}

export function getImageDimensions(file) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = URL.createObjectURL(file);
  });
}

export function getLastMessagePreview(room) {
  const timeline = room.getLiveTimeline();
  const events = timeline.getEvents();
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (event.getType() === "m.room.message") {
      const content = event.getContent();
      if (content.msgtype === "m.image") return content.filename && content.body !== content.filename ? `📷 ${content.body}` : "📷 Bild";
      if (content.msgtype === "m.video") return content.filename && content.body !== content.filename ? `🎬 ${content.body}` : "🎬 Video";
      if (content.msgtype === "m.audio") return "🎵 Audio";
      if (content.msgtype === "m.file") return `📎 ${content.filename || content.body || "Datei"}`;
      
      const bodyText = stripReplyFallback(content);
      return escapeHtml(bodyText.length > 40 ? bodyText.substring(0, 40) + "…" : bodyText);
    }
  }
  return "";
}
